(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('AnalysisRequest', ['$injector', function($injector) {
            const $q = $injector.get('$q');
            const $log = $injector.get('$log');
            const jobStatus = $injector.get('jobStatus');
            const InitPollJob = $injector.get('InitPollJob');

            /**
             * @ngdoc service
             * @name AnalysisRequest
             * @description ...
             */
            return class AnalysisRequest {
                constructor(requestObject, options) {
                    /**
                     * The main deferred instance to be resolved or rejected
                     * @type {Deferred}
                     */
                    const defer = $q.defer();

                    /**
                     * The timeout deferred instance used to trigger http abort operations
                     * @type {Deferred}
                     */
                    const timeoutDefer = $q.defer();

                    /**
                     * The timeout prmoise used to trigger http abort operations
                     * @type {Promise}
                     */
                    this.timeout = timeoutDefer.promise;

                    /**
                     * Inherit a fresh promise
                     */
                    Object.assign(this, defer);

                    /**
                     * Decorator for defer's resolve
                     * @param   {*} data
                     * @returns {*}
                     */
                    this.resolve = (data) => {
                        this.setPolling(false)
                            .setStatus(jobStatus.DONE);

                        return defer.resolve(data);
                    };

                    /**
                     * Decorator for defer's reject
                     * @param   {*} data
                     * @param   {Object=} status_ An optional custom status.
                     * @returns {*}
                     */
                    this.reject = (data, status_ = null) => {
                        const status = jobStatus.getStatus(status_) || jobStatus.getStatus(data) || jobStatus.UNKNOWN_SERVER_ERROR;

                        this.setPolling(false).setStatus(status);

                        $log.info(`Rejected analysis request with status ${JSON.stringify(status)} due to`, data);

                        return defer.reject(_.merge({}, data, status));
                    };

                    /**
                     * Decorator for defer's notify
                     * @param   {*} data
                     * @param   {*=} notification
                     * @returns {*}
                     */
                    this.notify = (data, notification = null) => {
                        //@todo: Is this the best way to define polling?
                        const polling = jobStatus.isJobRunning(data);
                        const status = jobStatus.getStatus(data) || jobStatus.UNKNOWN_SERVER_ERROR;

                        this.setPolling(polling).setStatus(status);

                        return defer.notify(notification);
                    };

                    /**
                     * The job id of this analysis request
                     * @type {string|null}
                     */
                    this.jobId = null;

                    /**
                     * Misc options hash
                     * @type {Object}
                     */
                    this.options = _.merge({polling: true}, options, {inheritedOptions: _.pick(requestObject, ['withCredentials'])});

                    /**
                     * The result type of this request i.e. DATA_ONLY, CSV, PDF
                     * @type {string}
                     */
                    this.resultType = _.get(requestObject, 'data.resultType');

                    /**
                     * The url to GET the result from the cache
                     * @type {string|null}
                     */
                    this.cacheUrl = _.get(this.options, 'cacheUrl', null);

                    /**
                     * The init poll job
                     * @type {InitPollJob}
                     */
                    this.initPollJob = new InitPollJob(this.resultType, _.chain(this.cacheUrl).split('/').last().value());

                    /**
                     * Unique identifier of the parent of the job
                     * @type {string}
                     */
                    this.parentJobId = this.initPollJob.id;

                    /**
                     * Ajax call options to fetch data for this request
                     * @type {Object}
                     */
                    this.requestObject = _.chain(requestObject)
                        .cloneDeep()
                        .set('timeout', this.initPollJob.promise)
                        .value();

                    /**
                     * Initial status of the job - initialy inherited from the init poll job
                     * @type {Object}
                     */
                    this.status = this.initPollJob.status;

                    /**
                     * Indicates if the job is currently polling or not
                     * @type {boolean}
                     */
                    this.isPolling = false;

                    /**
                     * The analysis snapshot that triggered this request
                     * @type {Object|null}
                     * @private
                     */
                    this.snapshot_ = null;

                    /**
                     * The empty response hash
                     * @type {Object}
                     */
                    this.emptyResponse = null;
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#setJobId
                 * @description Plain setter
                 * @param   {string} jobId
                 * @returns {AnalysisRequest}
                 */
                setJobId(jobId) {
                    this.jobId = jobId;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#setStatus
                 * @description Plain setter
                 * @param   {Object} status
                 * @returns {AnalysisRequest}
                 */
                setStatus(status) {
                    this.status = status;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#setPolling
                 * @description Plain setter
                 * @param   {boolean} isPolling
                 * @returns {AnalysisRequest}
                 */
                setPolling(isPolling = false) {
                    this.isPolling = isPolling;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#setEmptyResponse
                 * @param   {Object} emptyResponse
                 * @returns {AnalysisRequest}
                 */
                setEmptyResponse(emptyResponse) {
                    this.emptyResponse = emptyResponse;
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#setSnapshot
                 * @description Plain setter
                 * @param   {Object} snapshot
                 * @returns {AnalysisRequest}
                 */
                setSnapshot(snapshot) {
                    this.snapshot_ = _.cloneDeep(snapshot);
                    return this;
                }

                /**
                 * @ngdoc property
                 * @name AnalysisRequest#snapshot
                 * @description A clone of the analysis snapshot that triggered this request
                 * @returns {Object}
                 */
                get snapshot() {
                    return _.cloneDeep(this.snapshot_);
                }

                /**
                 * @ngdoc property
                 * @name AnalysisRequest#requestObjectCache
                 * @description Ajax call options to fetch data from the cache url
                 * @returns {Object}
                 */
                get requestObjectCache() {
                    return _.chain(this.requestObject)
                            .cloneDeep()
                            .set('timeout', this.initPollJob.promise)
                            .set('url', this.cacheUrl)
                            .set('method', 'GET')
                            .set('data', null)
                            .value();
                }

                /**
                 * @ngdoc property
                 * @name AnalysisRequest#query
                 * @description Returns the first and only query of an analysis request.
                 * @returns {Object|null}
                 */
                get query() {
                    return _.get(this.requestObject, 'data.calculations.0') || null;
                }

                /**
                 * @ngdoc property
                 * @name AnalysisRequest#cacheBustOptions
                 * @description Returns the options for this analysis request omitting the cacheUrl
                 * @returns {Object}
                 */
                get cacheBustOptions() {
                    return _.omit(this.options, 'cacheUrl');
                }

                /**
                 * @ngdoc method
                 * @name AnalysisRequest#abort
                 * @description Will abort the initPollJob associated with an analysis request and
                 * if it is an analysis cache request - i.e. not a polling analysis request - the
                 * init poll job will be resolved so as to abort the get http call.
                 * @returns {AnalysisRequest}
                 */
                abort() {
                    this.initPollJob.abort();

                    if (this.cacheUrl) {
                        this.initPollJob.resolve();
                    }

                    return this;
                }
            };
        }]);
})(window.angular, window._);
